<script setup>
import i18next from 'i18next';
import { computed, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { MarketOverview, TickerTape } from 'vue-tradingview-widgets';
import altcoins from '../../data/altcoins.json';
import forex from '../../data/forex.json';
import indizes from '../../data/indizes.json';
import symbols from '../../data/symbols.json';
import { fetchData } from '../../fetchData';
import { useCcm19Global } from '../../useCcm19Global';
import BreakingNews from './BreakingNews.vue';
import CategoryFilter from './CategoryFilter.vue';
import FinancialEducation from './FinancialEducation.vue';
import Highlights from './Highlights.vue';
import Intro from './Intro.vue';
import NewsFilter from './NewsFilter.vue';
import NewsList from './NewsList.vue';
import ProductCategory from './ProductCategory.vue';
import SearchText from './SearchText.vue';
import VideoList from './VideoList.vue';

const router = useRouter();
const route = useRoute();

const postCategoryIds = ref([]);
const searchPhrase = ref('');
const sortBy = ref('published_at:desc');
const authorIds = ref([]);
const filtersInitialized = ref(false);
const filters = ref({ users: null, postCategories: null });
const finanzBildungCategoryId = ref(null);
const date = ref([]);
const hasDiscountBanner = ref(false);

const isAnyFilterActive = computed(() => {
    return (
        (postCategoryIds.value && postCategoryIds.value.length > 0) ||
        sortBy.value !== 'published_at:desc' ||
        (authorIds.value && authorIds.value.length > 0) ||
        (date.value && date.value.length > 0)
    );
});

const updateUrlQuery = () => {
    const query = {};

    if (postCategoryIds.value.length) {
        query.post_categories_id = postCategoryIds.value;
    }

    if (searchPhrase.value) {
        query.search = searchPhrase.value;
    }

    if (sortBy.value !== 'published_at:desc') {
        query.sort_by = sortBy.value;
    }

    if (authorIds.value.length) {
        query.authors = authorIds.value;
    }

    if (date.value && date.value.length === 2) {
        query.start_date = date.value[0] ? new Date(date.value[0]).toISOString().split('T')[0] : undefined;
        query.end_date = date.value[1] ? new Date(date.value[1]).toISOString().split('T')[0] : undefined;
    }

    router.replace({ query });
};

const initializeFiltersFromUrl = () => {
    const query = route.query;

    if (query.post_categories_id) {
        const cats = Array.isArray(query.post_categories_id) ? query.post_categories_id : [query.post_categories_id];
        postCategoryIds.value = cats.map((c) => Number(c));
    }

    if (query.search) {
        searchPhrase.value = query.search;
    }

    if (query.sort_by) {
        sortBy.value = query.sort_by;
    }

    if (query.authors) {
        const auths = Array.isArray(query.authors) ? query.authors : [query.authors];
        authorIds.value = auths.map((a) => String(a));
    }

    if (query.start_date || query.end_date) {
        date.value = [query.start_date ? new Date(query.start_date) : null, query.end_date ? new Date(query.end_date) : null].filter(Boolean);
    }
};

const fetchFilters = async () => {
    try {
        const { result } = await fetchData('/api/v1/posts/filters');

        filters.value.users = result.users;
        filters.value.postCategories = result.postCategories;
        finanzBildungCategoryId.value = result.postCategories.data.find((cat) => cat.name === 'Finanzbildung')?.id;
    } catch (e) {
        console.error('Error fetching filter data:', e);
    }
};

const reset = () => {
    postCategoryIds.value = [];
    searchPhrase.value = '';
    sortBy.value = 'published_at:desc';
    authorIds.value = [];
    date.value = [];
};

let debounceTimeout = null;

const debouncedUpdateUrlQuery = () => {
    clearTimeout(debounceTimeout);
    debounceTimeout = setTimeout(() => {
        updateUrlQuery();
    }, 900);
};

watch(
    [postCategoryIds, searchPhrase, sortBy, authorIds, date],
    () => {
        debouncedUpdateUrlQuery();
    },
    { deep: true }
);

onMounted(() => {
    initializeFiltersFromUrl();
    fetchFilters();
    filtersInitialized.value = true;
    hasDiscountBanner.value = !!document.getElementById('#discountBanner');

    const obs = new MutationObserver(() => {
        hasDiscountBanner.value = !!document.getElementById('#discountBanner');
    });
    obs.observe(document.body, { childList: true, subtree: true });
});
const { onReady, tradingView } = useCcm19Global();
onReady(() => {});
</script>

<template>
    <div>
        <TickerTape
            v-if="tradingView"
            :options="{
                symbols: symbols,
                showSymbolLogo: true,
                colorTheme: 'light',
                isTransparent: false,
                displayMode: 'adaptive',
                locale: 'de'
            }"
        />

        <div class="container mx-auto">
            <Intro class="my-4" />

            <Highlights :highlighted="true" class="my-4" />

            <div class="my-4">
                <p class="text-xl font-semibold">
                    {{ $t('plain_search') }}
                </p>
                <CategoryFilter v-model="postCategoryIds" class="mt-4" />
            </div>

            <div id="news-wrapper" class="flex flex-col lg:flex-row gap-2 my-4 mb-8">
                <div class="w-full lg:w-2/3">
                    <div :class="['grid grid-cols-1 lg:grid-cols-2 lg:gap-3 my-4 sticky z-10', hasDiscountBanner ? 'top-32' : 'top-20']">
                        <SearchText v-model:text="searchPhrase" />
                        <NewsFilter
                            v-model:sort-by="sortBy"
                            v-model:author-ids="authorIds"
                            v-model:date="date"
                            :filters="filters"
                            :is-any-filter-active="isAnyFilterActive"
                            @reset="reset"
                        />
                    </div>
                    <NewsList
                        v-if="filtersInitialized"
                        :post-category-ids="postCategoryIds"
                        :search-phrase="searchPhrase"
                        :sort-by="sortBy"
                        :author-ids="authorIds"
                        :date-range="date"
                    />
                </div>
                <div class="w-full lg:w-1/3 lg:pl-2 my-4 space-y-4">
                    <div class="border-2 border-blue p-2 h-[32.5rem] bg-white rounded-lg shadow-lg flex flex-col">
                        <div class="flex flex-row gap-2 justify-between items-center">
                            <strong> {{ $t('plain_breaking_news') }} </strong>
                            <router-link
                                to="/hkcmnews/breaking-news"
                                class="inline-flex items-center gap-2 hover:text-blue hover:bg-gray-100 text-sm transition-all ease-in-out p-1 px-2 rounded-md cursor-pointer"
                            >
                                <span> {{ $t('plain_all') }} {{ $t('plain_breaking_news') }} </span>
                                <svg class="h-3" viewBox="0 0 12 6" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M1.39685 3H10.2857H1.39685Z" fill="currentColor" />
                                    <path d="M1.39685 3H10.2857" stroke="currentColor" stroke-width="0.47619" />
                                    <path d="M7.42859 0.428589L10.2857 3.00002L7.42859 5.57145" stroke="currentColor" stroke-width="0.47619" stroke-linecap="square" />
                                </svg>
                            </router-link>
                        </div>
                        <BreakingNews />
                    </div>
                    <div v-if="finanzBildungCategoryId" class="border-2 border-gray-300 p-2 h-[28rem] bg-white rounded-lg shadow-lg flex flex-col">
                        <div class="flex flex-row gap-2 justify-between items-center">
                            <strong> {{ $t('news_financial_education') }} </strong>
                            <button
                                class="inline-flex items-center gap-2 hover:text-blue hover:bg-gray-100 text-sm transition-all ease-in-out p-1 px-2 rounded-md cursor-pointer"
                                @click="
                                    () => {
                                        postCategoryIds = [finanzBildungCategoryId];
                                    }
                                "
                            >
                                <span> {{ $t('plain_all') }} {{ $t('published_articles') }} </span>
                                <svg class="h-3" viewBox="0 0 12 6" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M1.39685 3H10.2857H1.39685Z" fill="currentColor" />
                                    <path d="M1.39685 3H10.2857" stroke="currentColor" stroke-width="0.47619" />
                                    <path d="M7.42859 0.428589L10.2857 3.00002L7.42859 5.57145" stroke="currentColor" stroke-width="0.47619" stroke-linecap="square" />
                                </svg>
                            </button>
                        </div>
                        <FinancialEducation />
                    </div>
                    <div v-if="$getRegion() === 'de'" class="border-2 border-gray-300 p-2 h-[28rem] bg-white rounded-lg shadow-lg flex flex-col">
                        <div class="flex flex-row gap-2 justify-between items-center">
                            <strong> HKCM Videos </strong>
                            <a
                                href="https://www.youtube.com/@hkcm"
                                target="_blank"
                                class="inline-flex items-center gap-2 hover:text-blue hover:bg-gray-100 text-sm transition-all ease-in-out p-1 px-2 rounded-md cursor-pointer"
                            >
                                <span> {{ $t('plain_all') }} Videos </span>
                                <svg class="h-3" viewBox="0 0 12 6" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M1.39685 3H10.2857H1.39685Z" fill="currentColor" />
                                    <path d="M1.39685 3H10.2857" stroke="currentColor" stroke-width="0.47619" />
                                    <path d="M7.42859 0.428589L10.2857 3.00002L7.42859 5.57145" stroke="currentColor" stroke-width="0.47619" stroke-linecap="square" />
                                </svg>
                            </a>
                        </div>
                        <VideoList
                            :params="{
                                'sort[by]': 'created_at',
                                'sort[order]': 'desc',
                                playlist_id: $getRegion() !== 'de' ? 'UUvLnTGJQthqju38QPtaD64Q' : 'UU3AdN1bEmEonuSwXNS8LixQ',
                                key: $getRegion() !== 'de' ? 'UUvLnTGJQthqju38QPtaD64Q' : 'UU3AdN1bEmEonuSwXNS8LixQ',
                                type: ['youtube']
                            }"
                        />
                    </div>
                    <div v-if="$getRegion() === 'de'" class="border-2 border-gray-300 p-2 h-[28rem] bg-white rounded-lg shadow-lg flex flex-col">
                        <div class="flex flex-row gap-2 justify-between items-center">
                            <strong> Phantom Videos </strong>
                            <a
                                href="https://www.youtube.com/@phantombyhkcm"
                                target="_blank"
                                class="inline-flex items-center gap-2 hover:text-blue hover:bg-gray-100 text-sm transition-all ease-in-out p-1 px-2 rounded-md cursor-pointer"
                            >
                                <span> {{ $t('plain_all') }} Videos </span>
                                <svg class="h-3" viewBox="0 0 12 6" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M1.39685 3H10.2857H1.39685Z" fill="currentColor" />
                                    <path d="M1.39685 3H10.2857" stroke="currentColor" stroke-width="0.47619" />
                                    <path d="M7.42859 0.428589L10.2857 3.00002L7.42859 5.57145" stroke="currentColor" stroke-width="0.47619" stroke-linecap="square" />
                                </svg>
                            </a>
                        </div>
                        <VideoList
                            :params="{
                                'sort[by]': 'created_at',
                                'sort[order]': 'desc',
                                playlist_id: 'PLsIDvtZfmyZQe9Nmi3sVxwhzn8151Cmpd',
                                key: 'PLsIDvtZfmyZQe9Nmi3sVxwhzn8151Cmpd',
                                type: ['youtube']
                            }"
                        />
                    </div>
                    <div v-if="$getRegion() === 'de'" class="border-2 border-gray-300 p-2 h-[28rem] bg-white rounded-lg shadow-lg flex flex-col">
                        <div class="flex flex-row gap-2 justify-between items-center">
                            <strong> Charts & Cars by Phillip Klinkmüller </strong>
                            <a
                                href="https://www.youtube.com/@ChartsCarsbyPhilipKlinkm%C3%BCller"
                                target="_blank"
                                class="inline-flex items-center gap-2 hover:text-blue hover:bg-gray-100 text-sm transition-all ease-in-out p-1 px-2 rounded-md cursor-pointer"
                            >
                                <span> {{ $t('plain_all') }} Videos </span>
                                <svg class="h-3" viewBox="0 0 12 6" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M1.39685 3H10.2857H1.39685Z" fill="currentColor" />
                                    <path d="M1.39685 3H10.2857" stroke="currentColor" stroke-width="0.47619" />
                                    <path d="M7.42859 0.428589L10.2857 3.00002L7.42859 5.57145" stroke="currentColor" stroke-width="0.47619" stroke-linecap="square" />
                                </svg>
                            </a>
                        </div>
                        <VideoList
                            :params="{
                                'sort[by]': 'created_at',
                                'sort[order]': 'desc',
                                playlist_id: 'PLpkkuYdgGRDlrtHT19AX5LSoVSUwLJlkJ',
                                key: 'PLpkkuYdgGRDlrtHT19AX5LSoVSUwLJlkJ',
                                type: ['youtube']
                            }"
                        />
                    </div>
                    <div class="border-2 border-gray-300 p-2 bg-white rounded-lg shadow-lg flex flex-col">
                        <div class="flex flex-row gap-2 justify-between items-center">
                            <strong> {{ $t('news_global_markets') }} </strong>
                            <router-link
                                to="/produkte"
                                class="inline-flex items-center gap-2 hover:text-blue hover:bg-gray-100 text-sm transition-all ease-in-out p-1 px-2 rounded-md cursor-pointer"
                            >
                                <span> {{ $t('news_our_products') }} </span>
                                <svg class="h-3" viewBox="0 0 12 6" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M1.39685 3H10.2857H1.39685Z" fill="currentColor" />
                                    <path d="M1.39685 3H10.2857" stroke="currentColor" stroke-width="0.47619" />
                                    <path d="M7.42859 0.428589L10.2857 3.00002L7.42859 5.57145" stroke="currentColor" stroke-width="0.47619" stroke-linecap="square" />
                                </svg>
                            </router-link>
                        </div>
                        <div class="relative">
                            <a
                                v-if="tradingView"
                                href="https://www.tradingview.com/?aff_id=23069&source=NEWS"
                                target="_blank"
                                rel="noopener noreferrer"
                                class="absolute top-2 right-2 text-[10px] bg-white/80 backdrop-blur-sm px-2 py-1 rounded-md shadow-sm hover:bg-white transition"
                            >
                                Try on TradingView →
                            </a>

                            <MarketOverview
                                v-if="tradingView"
                                class="block mt-2 -m-2 rounded-xl"
                                :options="{
                                    tabs: [
                                        { title: i18next.t('plain_indices'), symbols: indizes },
                                        { title: i18next.t('plain_commodities'), symbols: symbols },
                                        { title: 'Altcoins', symbols: altcoins },
                                        { title: 'Forex', symbols: forex }
                                    ],
                                    showSymbolLogo: true,
                                    colorTheme: 'light',
                                    isTransparent: false,
                                    displayMode: 'adaptive',
                                    locale: 'en'
                                }"
                            />
                            <div v-else class="h-96 bg-gray-50 rounded-lg flex flex-col items-center justify-center p-4 text-center">
                                <CookieReset />
                            </div>

                            <p v-if="tradingView" class="text-center text-gray-500 text-sm mt-2">
                                Powered by
                                <a
                                    href="https://www.tradingview.com/?aff_id=23069&source=NEWS"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    class="underline hover:text-blue-600 transition"
                                >
                                    TradingView
                                </a>
                            </p>
                        </div>
                    </div>
                    <div class="border-2 border-gray-300 p-2 bg-white rounded-lg shadow-lg flex flex-col">
                        <div class="flex flex-row gap-2 justify-between items-center">
                            <strong> {{ $t('news_our_products') }} </strong>
                            <router-link
                                to="/produkte"
                                class="inline-flex items-center gap-2 hover:text-blue hover:bg-gray-100 text-sm transition-all ease-in-out p-1 px-2 rounded-md cursor-pointer"
                            >
                                <span> {{ $t('plain_all_products') }} </span>
                                <svg class="h-3" viewBox="0 0 12 6" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M1.39685 3H10.2857H1.39685Z" fill="currentColor" />
                                    <path d="M1.39685 3H10.2857" stroke="currentColor" stroke-width="0.47619" />
                                    <path d="M7.42859 0.428589L10.2857 3.00002L7.42859 5.57145" stroke="currentColor" stroke-width="0.47619" stroke-linecap="square" />
                                </svg>
                            </router-link>
                        </div>
                        <ProductCategory />
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
